import { useEffect, useState } from 'react';
import type { AdminStoreReviews } from '@boardflight/shared';
import { api, loadSession, onSessionChange, signIn, signOut, type Session } from './api.js';
import { ApiError } from './api.js';
import { Accounts } from './sections/Accounts.js';
import { Teams } from './sections/Teams.js';
import { Games } from './sections/Games.js';
import { Features } from './sections/Features.js';
import { AiModels } from './sections/AiModels.js';
import { Assistant } from './sections/Assistant.js';
import { Analytics } from './sections/Analytics.js';
import { Active } from './sections/Active.js';
import { Sandboxes } from './sections/Sandboxes.js';
import { Pieces } from './sections/Pieces.js';
import { Onboarding } from './sections/Onboarding.js';
import { Creators } from './sections/Creators.js';
import { Reviews } from './sections/Reviews.js';
import { Releases } from './sections/Releases.js';
import { Figures } from './sections/Figures.js';
import { Orders } from './sections/Orders.js';
import { useAsync, zoneName } from './ui.js';

/**
 * The admin console (`docs/admin-plan.md` §5).
 *
 * Sixteen sections, addressable in the URL fragment because "look at this team's plan" is a thing
 * to paste into a message. No router: one level of sections and one of nesting do not need one, and
 * `apps/web`'s own router exists for reasons — a navigation guard for the playground — that have
 * no equivalent here.
 *
 * Down a sidebar in four groups rather than across a tab strip (owner, 2026-10-28): fourteen words
 * in a row were read by scanning all of them, and a group's heading says where to look.
 */

const SECTIONS = [
  { id: 'accounts', label: 'Accounts' },
  { id: 'teams', label: 'Teams' },
  // Between teams and features on purpose: a game is looked up from the team that owns it far
  // more often than from anywhere else, and the only write here is about one game's identity.
  { id: 'games', label: 'Games' },
  // Beside Games because it is about games, and the only tab with a queue in it — its label says
  // how long the queue is, so it is noticed from whichever tab is open (docs/store-review-plan.md).
  { id: 'reviews', label: 'Reviews' },
  // Beside Reviews: the other queue, of printed figures waiting for a print and a decision, with
  // which games may make them and what Meshy has left (docs/meshy-models-architecture.md §7).
  { id: 'figures', label: 'Figures' },
  // Beside Figures: the sets players bought, to print, paint and post (docs/meshy-models-architecture.md §9.4).
  { id: 'orders', label: 'Orders' },
  // Beside Reviews: the other tab whose button changes what people outside BoardFlight get — here,
  // which desktop app is downloaded and updated to (docs/client-release-plan.md §4).
  { id: 'releases', label: 'Releases' },
  { id: 'features', label: 'Features' },
  // Beside Features: the other list here that decides what every team can use — which models a
  // team's playground AI may run on (docs/playground-team-ai-plan.md §6).
  { id: 'ai-models', label: 'AI models' },
  // Beside AI models: the other AI this console sets up — the creators' assistant, which BoardFlight
  // pays for, and how it is used (docs/assistant-plan.md §11).
  { id: 'assistant', label: 'Assistant' },
  { id: 'analytics', label: 'Analytics' },
  // Beside Analytics: the same question asked of BoardFlight rather than of a game — how many
  // accounts and Boards were active by day, week and month (docs/admin-plan.md §3.5).
  { id: 'active', label: 'Active' },
  { id: 'sandboxes', label: 'Sandboxes' },
  // Last, with the other two read-only numbers screens: it answers a question about the world
  // rather than about any account in it, and there is nothing on it to press.
  { id: 'pieces', label: 'Pieces' },
  // Beside Pieces, and read-only like it: the Board client's own funnel rather than any account's.
  { id: 'onboarding', label: 'Onboarding' },
  // The dashboard's onboarding, beside the Board's: what new creators said about themselves.
  { id: 'creators', label: 'Creators' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

/** The sidebar's groups, in order. Every section is in exactly one. */
const GROUPS: { label: string; ids: readonly SectionId[] }[] = [
  { label: 'People', ids: ['accounts', 'teams', 'creators'] },
  { label: 'Games', ids: ['games', 'reviews', 'figures', 'orders', 'analytics', 'pieces'] },
  { label: 'Growth', ids: ['active', 'onboarding'] },
  { label: 'Platform', ids: ['releases', 'features', 'ai-models', 'assistant', 'sandboxes'] },
];

const labelOf = (id: SectionId): string => SECTIONS.find((entry) => entry.id === id)?.label ?? id;

/**
 * Where the dashboard lives — the mirror of `apps/web`'s `ADMIN_URL` (`docs/admin-plan.md`).
 *
 * A different Worker on a different URL, so the way back out is an ordinary external anchor and
 * the origin is compiled in rather than discovered. Unconditional, unlike the link the other way:
 * everybody who can see this bar has an account at boardflight.app, because that is the only place
 * an account is made.
 */
const DASHBOARD_URL = import.meta.env.VITE_WEB_BASE_URL ?? 'https://boardflight.app';

function useSection(): [SectionId, (next: SectionId) => void] {
  const read = (): SectionId => {
    // `?` ends the section too, so a link can carry a section's filter (`#accounts?excluded=1`).
    const head = window.location.hash.replace(/^#/, '').split(/[/?]/)[0] ?? '';
    return (SECTIONS.some((s) => s.id === head) ? head : 'accounts') as SectionId;
  };
  const [section, setSection] = useState<SectionId>(read);

  useEffect(() => {
    const onChange = () => setSection(read());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);

  return [
    section,
    (next) => {
      window.location.hash = next;
      setSection(next);
    },
  ];
}

export function App() {
  const [session, setSession] = useState<Session | null>(loadSession);
  useEffect(() => onSessionChange(setSession), []);

  if (!session) return <SignIn />;

  /*
   * Signed in, but not an admin.
   *
   * Reachable, because sign-in is the same sign-in as everywhere else (§D3a) and any account can
   * complete it on this origin. What it must not do is confirm anything: the message says the
   * account has no access and never that a console exists which somebody else can open, and every
   * route behind this screen would 404 identically anyway. The sign-out is the only way forward,
   * which is the honest one.
   */
  if (!session.admin) return <NoAccess email={session.email} />;

  return <Console session={session} />;
}

function Console({ session }: { session: Session }) {
  const [section, setSection] = useSection();
  // Read here rather than inside the tab, so the tab's label can carry the count from any section.
  // Re-read when the tab is opened, so a submission made since the page loaded is not missed.
  const reviews = useAsync(
    () => api<AdminStoreReviews>('/v1/admin/store-reviews'),
    [section === 'reviews'],
  );
  const waiting = reviews.data?.pending.length ?? 0;

  return (
    <div className="shell">
      <aside className="side">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true" />
          <span>BoardFlight <b>Admin</b></span>
        </div>
        <nav className="side-nav" aria-label="Sections">
          {GROUPS.map((group) => (
            <div key={group.label} className="side-group">
              <div className="side-group-label">{group.label}</div>
              {group.ids.map((id) => (
                <button
                  key={id}
                  className={id === section ? 'side-link on' : 'side-link'}
                  aria-current={id === section ? 'page' : undefined}
                  onClick={() => setSection(id)}
                >
                  <span>{labelOf(id)}</span>
                  {id === 'reviews' && waiting > 0 ? <span className="count">{waiting}</span> : null}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="side-foot">
          <div className="who" title={session.email}>{session.email}</div>
          {/*
           * Which clock every timestamp on every screen is on.
           *
           * The server sends epoch milliseconds and the browser renders them locally, which is the
           * right way round — but a bare local time is only honest if the reader knows it is local.
           * Said once, here, rather than as a suffix on a few hundred table cells.
           */}
          <div className="who muted" title="Every time on this page is shown in this zone.">
            Times in {zoneName()}
          </div>
          <div className="side-actions">
            {/*
             * The way back to the product, beside the other control that leaves the console.
             *
             * An anchor rather than a button with a handler, because it is a different origin: an
             * ordinary navigation that should middle-click and open-in-new-tab like one. `noreferrer`
             * keeps this hostname out of the dashboard's request logs, which is the same care the
             * link in the other direction takes (§D4).
             */}
            <a className="btn ghost" href={DASHBOARD_URL} rel="noreferrer">
              Dashboard ↗
            </a>
            <button className="ghost" onClick={() => void signOut()}>Sign out</button>
          </div>
        </div>
      </aside>

      <main className="main">
        <header className="page-head">
          <h1>{labelOf(section)}</h1>
        </header>
        {section === 'accounts' ? <Accounts /> : null}
        {section === 'teams' ? <Teams /> : null}
        {section === 'games' ? <Games /> : null}
        {section === 'reviews' ? <Reviews state={reviews} reload={reviews.reload} /> : null}
        {section === 'figures' ? <Figures /> : null}
        {section === 'orders' ? <Orders /> : null}
        {section === 'releases' ? <Releases /> : null}
        {section === 'features' ? <Features /> : null}
        {section === 'ai-models' ? <AiModels /> : null}
        {section === 'assistant' ? <Assistant /> : null}
        {section === 'analytics' ? <Analytics /> : null}
        {section === 'active' ? <Active /> : null}
        {section === 'sandboxes' ? <Sandboxes /> : null}
        {section === 'pieces' ? <Pieces /> : null}
        {section === 'onboarding' ? <Onboarding /> : null}
        {section === 'creators' ? <Creators /> : null}
      </main>
    </div>
  );
}

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <h1>BoardFlight <b>Admin</b></h1>
      {/* The same account, the same password, the same sessions table (§D3a). */}
      <p className="note">Sign in with your BoardFlight account.</p>
      <form onSubmit={submit}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          autoComplete="username"
          required
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoComplete="current-password"
          required
        />
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      {error ? <p className="error">{error}</p> : null}
    </div>
  );
}

function NoAccess({ email }: { email: string }) {
  return (
    <div className="signin">
      <h1>No access</h1>
      {/* Names the account and nothing else. Not "you are not an admin", which confirms admins. */}
      <p className="muted">{email} cannot open this page.</p>
      <button onClick={() => void signOut()}>Sign out</button>
    </div>
  );
}
